import React, { useState, useEffect } from 'react';
import {
  ArrowRight,
  Play,
  Zap,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  Trash2,
  Ban,
  HardDrive
} from 'lucide-react';
import './Sections.css';
import awsLogo from '../../assets/logos/aws-logo.svg';
import gcpLogo from '../../images/gcp-logo.png';
import azureLogo from '../../assets/logos/azure-logo.svg';

import { API_BASE_URL } from '../../config';

function MigrationsSection({ connections, jobs, onRefresh, loading, setLoading, showNotification }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState(new Set());

  const [formData, setFormData] = useState({
    name: '',
    source_connection_id: '',
    target_connection_id: ''
  });

  useEffect(() => {
    const interval = setInterval(() => {
      onRefresh();
    }, 5000);
    return () => clearInterval(interval);
  }, [onRefresh]);

  const toggleJobExpansion = (jobId) => {
    setExpandedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/migrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        showNotification('Migration job created and started!', 'success');
        setShowCreateModal(false);
        setFormData({ name: '', source_connection_id: '', target_connection_id: '' });
        onRefresh();
      } else {
        showNotification(data.error || 'Failed to create migration job', 'error');
      }
    } catch (error) {
      showNotification('Failed to create migration job', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this migration?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/migrations/${id}/cancel`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        showNotification('Migration cancelled successfully', 'info');
        onRefresh();
      } else {
        showNotification(data.error || 'Failed to cancel migration', 'error');
      }
    } catch (error) {
      showNotification('Failed to cancel migration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm('Are you sure you want to delete this migration record?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/migrations/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        showNotification('Migration record deleted', 'success');
        onRefresh();
      } else {
        showNotification(data.error || 'Failed to delete record', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete record', 'error');
    } finally {
      setLoading(false);
    }
  };


  const formatSpeed = (bytesPerSecond) => {
    if (!bytesPerSecond || bytesPerSecond === 0 || bytesPerSecond === '0') return '0 Mbps';

    // Convert Bytes per second to Megabits per second (Mbps)
    const mbps = (bytesPerSecond * 8) / (1000 * 1000);

    if (mbps >= 1) {
      return `${mbps.toFixed(2)} Mbps`;
    } else {
      // Show Kbps if less than 1 Mbps
      const kbps = (bytesPerSecond * 8) / 1000;
      return `${kbps.toFixed(2)} Kbps`;
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0 || bytes === '0') return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={16} />;
      case 'running': return <RefreshCw size={16} className="spin-smooth" />;
      case 'completed': return <CheckCircle size={16} />;
      case 'failed': return <XCircle size={16} />;
      case 'cancelled': return <AlertCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getConnectionName = (connectionId) => {
    const connection = connections.find(c => String(c.id) === String(connectionId));
    return connection ? connection.name : 'Unknown';
  };

  const renderConnectionPreview = (connectionId, fallback) => {
    const conn = connections.find(c => String(c.id) === String(connectionId));
    if (!conn) return <span className="placeholder">{fallback}</span>;

    const getLogo = (provider) => {
      switch (provider?.toLowerCase()) {
        case 'aws': return awsLogo;
        case 'gcp': return gcpLogo;
        case 'azure': return azureLogo;
        default: return null;
      }
    };

    const logo = getLogo(conn.cloud_provider);

    return (
      <div className="preview-item">
        {logo && (
          <div className="preview-logo">
            <img
              src={logo}
              alt={conn.cloud_provider}
              className={conn.cloud_provider?.toLowerCase() === 'aws' ? 'aws-logo-img aws-logo-theme-fix' : ''}
            />
          </div>
        )}
        <span className="connection-name">{conn.name}</span>
      </div>
    );
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <div className="title-group">
          <h2>Migration Jobs</h2>
          <span className="subtitle">Manage and monitor database transitions</span>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowCreateModal(true)}
          disabled={loading || connections.length < 2}
        >
          <Play size={16} />
          Create Migration
        </button>
      </div>

      {connections.length < 2 && (
        <div className="warning-message">
          <AlertTriangle size={20} />
          <span>You need at least 2 database connections to create a migration job.</span>
        </div>
      )}

      <div className="jobs-list">
        {jobs.map(job => (
          <div key={job.id} className={`migration-box ${expandedJobs.has(job.id) ? 'expanded' : ''}`}>
            <div className="migration-header-compact" onClick={() => toggleJobExpansion(job.id)}>
              <div className="main-info">
                <div className="name-group">
                  <h3>{job.name}</h3>
                  <div className="job-id-tag">
                    <span className="label">REF:</span>
                    <span className="value">{job.id}</span>
                  </div>
                </div>
                <div className="date-group">
                  <Clock size={12} style={{ marginRight: 6, opacity: 0.6 }} />
                  <span className="job-date">{formatDate(job.created_at)}</span>
                  {job.status === 'running' && (
                    <div className="compact-speed">
                      <Zap size={12} className="speed-icon" />
                      <span>{formatSpeed(job.speed_bps)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="status-group">
                <div className="header-actions">
                  {(job.status === 'running' || job.status === 'pending') && (
                    <button
                      className="btn-cancel"
                      onClick={(e) => { e.stopPropagation(); handleCancelJob(job.id); }}
                      disabled={loading}
                    >
                      <Ban size={14} />
                      Cancel
                    </button>
                  )}
                  {(job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') && (
                    <button
                      className="action-icon-btn delete"
                      onClick={(e) => { e.stopPropagation(); handleDeleteJob(job.id); }}
                      disabled={loading}
                      title="Delete Record"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                <div className={`status-badge status-color-${job.status}`}>
                  {getStatusIcon(job.status)}
                  <span>{job.status.toUpperCase()}</span>
                </div>
                <div className="expand-icon">
                  <ChevronDown size={20} />
                </div>
              </div>
            </div>

            {expandedJobs.has(job.id) && (
              <div className="migration-dropdown-content">
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Source</span>
                    <span className="value highlight">{getConnectionName(job.source_connection_id)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Target</span>
                    <span className="value highlight">{getConnectionName(job.target_connection_id)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Progress</span>
                    <div className="progress-stack">
                      <span className="value">{job.progress || 0}%</span>
                      <div className="mini-progress-bar">
                        <div
                          className={`progress-fill status-color-${job.status}`}
                          style={{ width: `${job.progress || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="label">Started</span>
                    <span className="value">{formatDate(job.started_at)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Data Migrated</span>
                    <span className="value highlight">
                      <HardDrive size={14} style={{ marginRight: 6, opacity: 0.7 }} />
                      {formatBytes(job.total_bytes)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Completed</span>
                    <span className="value">{formatDate(job.completed_at)}</span>
                  </div>
                  <div className="detail-item full-width">
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {jobs.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <Zap size={64} />
            </div>
            <h3>No migration jobs yet</h3>
            <p>Create your first migration job to get started</p>
            {connections.length >= 2 && (
              <button
                className="btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <Play size={16} />
                Create Migration
              </button>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create Migration Job</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="name">Migration Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Production Database Migration"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="source_connection_id">Source Connection</label>
                  <select
                    id="source_connection_id"
                    name="source_connection_id"
                    value={formData.source_connection_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select source connection</option>
                    {connections
                      .filter(connection => String(connection.id) !== String(formData.target_connection_id))
                      .map(connection => (
                        <option key={connection.id} value={connection.id}>
                          {connection.name} ({connection.type.toUpperCase()})
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="target_connection_id">Target Connection</label>
                  <select
                    id="target_connection_id"
                    name="target_connection_id"
                    value={formData.target_connection_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select target connection</option>
                    {connections
                      .filter(connection => String(connection.id) !== String(formData.source_connection_id))
                      .map(connection => (
                        <option key={connection.id} value={connection.id}>
                          {connection.name} ({connection.type.toUpperCase()})
                        </option>
                      ))}
                  </select>
                </div>
                <div className="migration-preview">
                  <h4>Migration Preview</h4>
                  <div className="preview-path">
                    {renderConnectionPreview(formData.source_connection_id, 'Source')}
                    <span className="arrow"><ArrowRight size={18} /></span>
                    {renderConnectionPreview(formData.target_connection_id, 'Target')}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading || !formData.source_connection_id || !formData.target_connection_id}>
                  {loading ? 'Creating...' : 'Start Migration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MigrationsSection;